'use client';

import { Suspense, lazy, useState } from 'react';
import { useTheme } from 'next-themes';
import { MainNavbar } from '@/components/layout/MainNavbar';
import { useCalculator } from '@/hooks/useCalculator';
import { useGlobalKeyboardShortcuts } from '@/hooks/useGlobalKeyboardShortcuts';
import { KeyboardShortcutsModal } from '@/components/ui/KeyboardShortcutsModal';
import { useLastCalculatorTab } from '@/lib/calculator-storage';
import { Calculator, ArrowRight } from 'lucide-react';
import Link from 'next/link';

// Lazy load heavy components
const AboutOverview = lazy(() => import('@/components/about/AboutOverview').then(m => ({ default: m.AboutOverview })));
const LazyFooter = lazy(() => import('@/components/Footer').then(m => ({ default: m.Footer })));

function CalculatorApp() {
  const { setTheme, theme } = useTheme();
  const calculator = useCalculator();
  const { lastCalculatorTab } = useLastCalculatorTab();
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);

  // Enable global keyboard shortcuts
  useGlobalKeyboardShortcuts({
    lastCalculatorTab,
    onShowShortcuts: () => {
      setShortcutsModalOpen(true);
    },
    onPrint: () => {
      window.print();
    },
    onThemeToggle: () => {
      setTheme(theme === 'dark' ? 'light' : 'dark');
    }
  });

  return (
    <>
      <div className="bg-gradient-to-br from-background via-surface to-primary-50 dark:from-background dark:via-surface dark:to-primary-50">
        {/* Header */}
        <MainNavbar 
          currentPage="home" 
          currency={calculator.currency}
          onCurrencyChange={calculator.setCurrency}
        />

        {/* Main Content */}
        <main id="main-content" className="container mx-auto px-4 py-8 pt-24 sm:pt-8 max-w-7xl">
          {/* About Content - Always shown */}
          <div style={{ minHeight: '400px' }}>
            <Suspense fallback={
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accessnow-orange mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading content...</p>
              </div>
            }>
              <AboutOverview 
                showCalculatorButton={true}
                className="mb-16"
              />
            </Suspense>
          </div>

          {/* Quick Start Calculator Button */}
          <div className="text-center mb-16" style={{ height: '120px' }}>
            <Link href="/calculator">
              <button className="inline-flex items-center gap-3 px-8 py-4 bg-accessnow-orange hover:bg-accessnow-orange/90 text-white rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                <Calculator className="h-6 w-6" aria-hidden="true" />
                Start Calculating ROI
                <ArrowRight className="h-6 w-6" aria-hidden="true" />
              </button>
            </Link>
            <p className="text-muted-accessible mt-4">
              Use our guided wizard to calculate your specific savings and ROI
            </p>
          </div>
        </main>
      </div>

      {/* Footer */}
      <Suspense fallback={<div className="h-24 bg-muted"></div>}>
        <LazyFooter 
          inputs={calculator.inputs}
          pricing={calculator.pricing}
          results={calculator.results}
          currency={calculator.currency}
          showDebugModal={true}
          showChangelog={true}
          showHomeLink={false}
        />
      </Suspense>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        open={shortcutsModalOpen}
        onOpenChange={setShortcutsModalOpen}
      />
    </>
  );
}

export default function Home() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-br from-background via-surface to-primary-50 dark:from-background dark:via-surface dark:to-primary-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accessnow-orange mx-auto"></div>
            <p className="text-muted-foreground">Loading AccessNow ROI Calculator...</p>
          </div>
        </div>
      </div>
    }>
      <CalculatorApp />
    </Suspense>
  );
}
